import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useMemo, useState } from "react";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H3 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Card } from "../../../components/Card";
import { Modal } from "../../../components/Modal";
import { NewProfileApplicationFrame } from "./NewProfileApplicationFrame";
import { ROUTE } from "../../../routes";
import { useCreateProfileContact } from "../../../data/useCreateProfileContact";
import { showError } from "../../../utils/error-handling";
import { useMandateTypes } from "../../../data/useMandateTypes";
import { useCreateIndividualsInvites } from "../../../data/useCreateIndividualsInvites";
import { useCreateEntity } from "../../../data/useCreateEntity";

const SoftCard = ({ children, p = 3 }: { children: any; p?: any }) => (
  <Card
    p={p}
    style={{
      borderRadius: 14,
      border: `1px solid ${theme.colors.divider}`,
      background: theme.colors.white,
      boxShadow: "none",
    }}
  >
    {children}
  </Card>
);

const Muted = ({ children }: { children: any }) => (
  <Paragraph mb={0} color={theme.colors.muted}>
    {children}
  </Paragraph>
);

export const SendInvitesStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const outlet = useOutletContext<any>();
  const profile = outlet?.profile;

  const state: any = location.state ?? {};
  const mandateId = Number(state?.mandateId);

  const { mutate: createProfileContact, isPending: isSavingContact } =
    useCreateProfileContact();

  const {
    mutate: createIndividualsInvites,
    isPending: isSendingIndividualInvite,
  } = useCreateIndividualsInvites();
  const { mutate: createEntity, isLoading: isCreatingEntity } =
    useCreateEntity();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const isEntity = profile?._type === "entity";

  const isSending =
    isSavingContact || isSendingIndividualInvite || isCreatingEntity;

  const profileName = isEntity
    ? profile?.name ?? "-"
    : [profile?.first_name, profile?.middle_name, profile?.last_name]
        .filter(Boolean)
        .join(" ") || "-";

  const addresses = profile?.address ?? [];
  const preferredAddressType = isEntity ? "Registered" : "Primary Residence";

  const registered =
    addresses.find((a: any) => a?._type?.name === preferredAddressType) ||
    addresses[0];

  const renderLine1 = (a: any) =>
    [
      a?.flat_appartment_subbuilding,
      a?.building_number,
      a?.building_name,
      a?.road_street,
    ]
      .filter(Boolean)
      .join(" ");

  const renderLine2 = (a: any) =>
    [a?.town_city, a?.state_province_name || a?.district]
      .filter(Boolean)
      .join(", ");

  const addressLine = [renderLine1(registered), renderLine2(registered)]
    .filter(Boolean)
    .join(", ");

  const postcode = registered?.post_zip_code ?? "-";

  const countryLabel =
    registered?.country_name ||
    registered?.country ||
    profile?.country_name ||
    "";

  const { data: mandateTypes } = useMandateTypes(
    profile?._type === "entity" || profile?._type === "individual"
      ? profile._type
      : undefined
  );

  const applicationTypeId = Number(state?.applicationType);

  const applicationTypeName = useMemo(() => {
    const list = mandateTypes?.data ?? [];
    return list.find((m: any) => m.id === applicationTypeId)?.name;
  }, [mandateTypes, applicationTypeId]);

  const requestedInfo =
    state?.applicationTypeLabel ||
    state?.applicationTypeName ||
    applicationTypeName ||
    "-";

  // ✅ Individual flow: fall back to profile.email since confirm-email step is removed
  const contactEmail = state?.contactEmail || profile?.email || "";

  const handleSaveContact = (
    onSuccess?: () => void,
    errorMessage = "Failed to add contact.",
  ) => {
    if (!contactEmail) return;

    createProfileContact(
      { profileId, email: contactEmail },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (err: any) => {
          showError(err, errorMessage);
        },
      }
    );
  };

  const handleCreateEntity = (onSuccess?: () => void) => {
    if (!state.entityPayload) {
      setIsConfirmModalOpen(true);
      return;
    }

    createEntity(
      {
        ...state.entityPayload,
        email: contactEmail,
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (err: any) => {
          showError(err, "Failed to create the entity.");
        },
      },
    );
  };

  const handleSendInvite = () => {
    if (isEntity) {
      handleCreateEntity(() => {
        handleSaveContact(
          () => setIsConfirmModalOpen(true),
          "Entity created, but failed to add contact.",
        );
      });
      return;
    }

    createIndividualsInvites(
      {
        individual_ids: [state.createdRecordId],
        id: mandateId,
      },
      {
        onSuccess: () => {
          setIsConfirmModalOpen(true);
        },
        onError: (err: any) => {
          setIsConfirmModalOpen(false);
          showError(err, "A problem occurred while sending invite(s).");
        },
      }
    );
  };

  const goToProfile = () => {
    setIsConfirmModalOpen(false);
    navigate(ROUTE.PROFILE_DETAIL_VIEW.replace(":id", profileId.toString()));
  };

  return (
    <NewProfileApplicationFrame step="sendInvites">
      <Box mt={2}>
        <Box width={510}>
        <H3>Send Invite</H3>

        <Box mt={3}>
          <Paragraph>{isEntity ? "Entity Details:" : "Individual Details:"}</Paragraph>
          <SoftCard>
            <Paragraph fontSize={2} fontWeight={700}>
              {profileName}
            </Paragraph>
            <Paragraph fontSize="0">{preferredAddressType} address</Paragraph>
            <Muted>
              {addressLine && postcode
                ? `${addressLine}, ${postcode}`
                : "Not provided"}
            </Muted>

            {!!countryLabel && (
              <Flex alignItems="center" gap="8" mt={2}>
                {registered?.country_flag && (
                  <Box backgroundColor="gray.10" borderRadius="50%" p="1">
                    <img
                      src={`data:image/png;base64,${registered.country_flag}`}
                      alt="flag"
                      style={{
                        display: "block",
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                      }}
                    />
                  </Box>
                )}

                <Paragraph mt={0} mb={0} fontWeight={600}>
                  {countryLabel}
                </Paragraph>
              </Flex>
            )}

            {isEntity && (
              profile?.registration_number ? (
                <Paragraph
                  mt={2}
                  mb={0}
                  color={theme.colors.muted}
                  fontWeight={600}
                >
                  {profile.registration_number}
                </Paragraph>
              ) : (
                <>
                  <Paragraph fontSize="0" mt={2}>
                    Registration number
                  </Paragraph>
                  <Muted>-</Muted>
                </>
              )
            )}
          </SoftCard>
        </Box>

        <Box mt={3}>
          <Paragraph>Application Type:</Paragraph>
          <SoftCard p={2}>
            <Paragraph fontSize={2} fontWeight={700}>
              {requestedInfo}
            </Paragraph>
          </SoftCard>
        </Box>

        <Box mt={3}>
          <Paragraph>Invite will be sent to:</Paragraph>
          <SoftCard p={2}>
            <Paragraph fontSize={2} fontWeight={700}>
              {contactEmail || "-"}
            </Paragraph>
          </SoftCard>

          {!isEntity && !contactEmail && (
            <Paragraph mt={2} color="#B91C1C">
              This individual has no email on their profile. Add an email to send an invite.
            </Paragraph>
          )}
        </Box>

        <Flex justifyContent="space-between" alignItems="center" mt={4}>
          <Button
            variant="text"
            onClick={() => {
              const backRoute = isEntity
                ? ROUTE.PROFILE_APPLICATION_ADD_CONTACT
                : ROUTE.PROFILE_APPLICATION_APPLICATION_TYPE; // ✅ use your actual constant

              navigate(backRoute.replace(":id", profileId.toString()), { state });
            }}
            isDisabled={isSending}
          >
            Back
          </Button>

          <Flex gap="12" alignItems="center">
            <Button onClick={handleSendInvite} isDisabled={!contactEmail || isSending}>
              {isSending ? "Sending..." : "Send invite"}
            </Button>
          </Flex>
        </Flex>
        </Box>
      </Box>

      <Modal
        isOpen={isConfirmModalOpen}
        onClose={goToProfile}
        title="Your invitation has been sent successfully!"
        content="Thank you for sending this application request. An invite has been sent to the email address provided. "
        primaryButtonText="Close"
        primaryButtonVariant="primary"
        onModalConfirmClick={goToProfile}
        width="450px"
        hasIcon={true}
        isSuccessful={true}
      />
    </NewProfileApplicationFrame>
  );
};
